import { notEmpty } from "@fidget/common/utils/not-empty";
import { omit } from "lodash";
import { useState } from "react";

export interface ActivatedParams<T> {
  filterId: string;
  predicate(t: T): boolean;
  description?: string;
  content?: string;
  /** If antagonist filter gets activated, this filter will get deactivated and vice versa */
  antagonistId?: string;
}

export interface FiltersState<T> {
  [id: string]: ActivatedParams<T>;
}

export const useFiltersMap = <T extends unknown>(
  initial: FiltersState<T>,
  persistentInitial: FiltersState<T> = {}
) => {
  const [filters, setFilters] = useState({ ...initial, ...persistentInitial });
  const [persistent, setPersistent] = useState(persistentInitial);

  const activateFilter = (params: ActivatedParams<T>) => {
    setFilters((current) => {
      if (params.antagonistId) {
        const { [params.antagonistId]: v, ...rest } = current;

        return { ...rest, [params.filterId]: params };
      }

      return { ...current, [params.filterId]: params };
    });
  };

  const activateFilters = (state: FiltersState<T>) => {
    const antagonistIds = Object.values(state)
      .map((v) => v.antagonistId)
      .filter(notEmpty);
    const persistentFilters = omit(filters, antagonistIds);

    setFilters({ ...persistentFilters, ...state });
  };

  const deactivateFilter = (filterId: string) => {
    setFilters((current) => {
      const { [filterId]: v, ...rest } = current;
      if (v!.antagonistId && persistent[v!.antagonistId]) {
        return { [v!.antagonistId]: persistent[v!.antagonistId]!, ...rest };
      }
      return rest;
    });
  };

  const clearFilters = () => {
    setFilters(persistent);
  };

  const updatePersistent = (persistentFilters: FiltersState<T>) => {
    setPersistent(persistentFilters);
    activateFilters(persistentFilters);
  };

  return {
    filters,
    activateFilter,
    activateFilters,
    deactivateFilter,
    clearFilters,
    updatePersistent
  };
};
